/**
 * Permission catalog — single source of truth for the `aid` app.
 *
 * Format: `<recurso>:<accion>`. Values match what the frontend checks via
 * `useHasPermission('dominio:accion')` and what the backend enforces with
 * the `PermissionsGuard`.
 */
export enum Permission {
  // Dashboard
  DASHBOARD_READ = 'dashboard:read',
  // Usuarios
  USERS_READ = 'users:read',
  USERS_CREATE = 'users:create',
  USERS_UPDATE = 'users:update',
  USERS_DELETE = 'users:delete',
  // Roles
  ROLES_READ = 'roles:read',
  ROLES_CREATE = 'roles:create',
  ROLES_UPDATE = 'roles:update',
  ROLES_DELETE = 'roles:delete',
  // Solicitudes de acceso
  SOLICITUDES_READ = 'solicitudes:read',
  SOLICITUDES_APPROVE = 'solicitudes:approve',
  SOLICITUDES_REJECT = 'solicitudes:reject',
  // Clientes
  CLIENTES_READ = 'clientes:read',
  ESTADO_CUENTA_READ = 'estado-cuenta:read',
  CLIENTES_CREATE = 'clientes:create',
  CLIENTES_UPDATE = 'clientes:update',
  CLIENTES_DELETE = 'clientes:delete',
  // Servicios
  SERVICIOS_READ = 'servicios:read',
  SERVICIOS_CREATE = 'servicios:create',
  SERVICIOS_UPDATE = 'servicios:update',
  SERVICIOS_DELETE = 'servicios:delete',
  // Cobros
  COBROS_READ = 'cobros:read',
  COBROS_CREATE = 'cobros:create',
  COBROS_UPDATE = 'cobros:update',
  COBROS_DELETE = 'cobros:delete',
  COBROS_GENERAR = 'cobros:generar',
  COBROS_ANULAR = 'cobros:anular',
  // Pagos
  PAGOS_READ = 'pagos:read',
  PAGOS_CREATE = 'pagos:create',
  PAGOS_ANULAR = 'pagos:anular',
  // Excepciones
  EXCEPCIONES_READ = 'excepciones:read',
  EXCEPCIONES_CREATE = 'excepciones:create',
  EXCEPCIONES_UPDATE = 'excepciones:update',
  EXCEPCIONES_DELETE = 'excepciones:delete',
  // Visitas
  VISITAS_READ = 'visitas:read',
  VISITAS_CREATE = 'visitas:create',
  // Catálogos
  CATALOGOS_READ = 'catalogos:read',
  CATALOGOS_CREATE = 'catalogos:create',
  CATALOGOS_UPDATE = 'catalogos:update',
  CATALOGOS_DELETE = 'catalogos:delete',
  // Planes
  PLANES_READ = 'planes:read',
  PLANES_CREATE = 'planes:create',
  PLANES_UPDATE = 'planes:update',
  PLANES_DELETE = 'planes:delete',
  // Tipos de servicio
  TIPOS_SERVICIO_READ = 'tipos-servicio:read',
  TIPOS_SERVICIO_CREATE = 'tipos-servicio:create',
  TIPOS_SERVICIO_UPDATE = 'tipos-servicio:update',
  TIPOS_SERVICIO_DELETE = 'tipos-servicio:delete',
  // Comisiones
  COMISIONES_READ = 'comisiones:read',
  // Cobrador (pantalla de cobro del terreno)
  COBRADOR_READ = 'cobrador:read',
}

export const ALL_PERMISSIONS: readonly Permission[] = [
  // Dashboard
  Permission.DASHBOARD_READ,
  // Usuarios
  Permission.USERS_READ,
  Permission.USERS_CREATE,
  Permission.USERS_UPDATE,
  Permission.USERS_DELETE,
  // Roles
  Permission.ROLES_READ,
  Permission.ROLES_CREATE,
  Permission.ROLES_UPDATE,
  Permission.ROLES_DELETE,
  // Solicitudes de acceso
  Permission.SOLICITUDES_READ,
  Permission.SOLICITUDES_APPROVE,
  Permission.SOLICITUDES_REJECT,
  // Clientes
  Permission.CLIENTES_READ,
  Permission.ESTADO_CUENTA_READ,
  Permission.CLIENTES_CREATE,
  Permission.CLIENTES_UPDATE,
  Permission.CLIENTES_DELETE,
  // Servicios
  Permission.SERVICIOS_READ,
  Permission.SERVICIOS_CREATE,
  Permission.SERVICIOS_UPDATE,
  Permission.SERVICIOS_DELETE,
  // Cobros
  Permission.COBROS_READ,
  Permission.COBROS_CREATE,
  Permission.COBROS_UPDATE,
  Permission.COBROS_DELETE,
  Permission.COBROS_GENERAR,
  Permission.COBROS_ANULAR,
  // Pagos
  Permission.PAGOS_READ,
  Permission.PAGOS_CREATE,
  Permission.PAGOS_ANULAR,
  // Excepciones
  Permission.EXCEPCIONES_READ,
  Permission.EXCEPCIONES_CREATE,
  Permission.EXCEPCIONES_UPDATE,
  Permission.EXCEPCIONES_DELETE,
  // Visitas
  Permission.VISITAS_READ,
  Permission.VISITAS_CREATE,
  // Catálogos
  Permission.CATALOGOS_READ,
  Permission.CATALOGOS_CREATE,
  Permission.CATALOGOS_UPDATE,
  Permission.CATALOGOS_DELETE,
  // Planes
  Permission.PLANES_READ,
  Permission.PLANES_CREATE,
  Permission.PLANES_UPDATE,
  Permission.PLANES_DELETE,
  // Tipos de servicio
  Permission.TIPOS_SERVICIO_READ,
  Permission.TIPOS_SERVICIO_CREATE,
  Permission.TIPOS_SERVICIO_UPDATE,
  Permission.TIPOS_SERVICIO_DELETE,
  // Comisiones
  Permission.COMISIONES_READ,
  // Cobrador
  Permission.COBRADOR_READ,
];

export const PERMISSION_DESCRIPTIONS: Record<Permission, string> = {
  // Dashboard
  [Permission.DASHBOARD_READ]: 'Ver dashboard',
  // Usuarios
  [Permission.USERS_READ]: 'Ver usuarios',
  [Permission.USERS_CREATE]: 'Crear usuarios',
  [Permission.USERS_UPDATE]: 'Editar usuarios',
  [Permission.USERS_DELETE]: 'Eliminar usuarios',
  // Roles
  [Permission.ROLES_READ]: 'Ver roles',
  [Permission.ROLES_CREATE]: 'Crear roles',
  [Permission.ROLES_UPDATE]: 'Editar roles y permisos',
  [Permission.ROLES_DELETE]: 'Eliminar roles',
  // Solicitudes de acceso
  [Permission.SOLICITUDES_READ]: 'Ver solicitudes de acceso',
  [Permission.SOLICITUDES_APPROVE]: 'Aprobar solicitudes de acceso',
  [Permission.SOLICITUDES_REJECT]: 'Rechazar solicitudes de acceso',
  // Clientes
  [Permission.CLIENTES_READ]: 'Ver clientes',
  [Permission.ESTADO_CUENTA_READ]: 'Ver estados de cuenta',
  [Permission.CLIENTES_CREATE]: 'Crear clientes',
  [Permission.CLIENTES_UPDATE]: 'Editar clientes',
  [Permission.CLIENTES_DELETE]: 'Eliminar clientes',
  // Servicios
  [Permission.SERVICIOS_READ]: 'Ver servicios',
  [Permission.SERVICIOS_CREATE]: 'Crear servicios',
  [Permission.SERVICIOS_UPDATE]: 'Editar servicios',
  [Permission.SERVICIOS_DELETE]: 'Eliminar servicios',
  // Cobros
  [Permission.COBROS_READ]: 'Ver facturas',
  [Permission.COBROS_CREATE]: 'Registrar facturas',
  [Permission.COBROS_UPDATE]: 'Editar facturas',
  [Permission.COBROS_DELETE]: 'Eliminar facturas',
  [Permission.COBROS_GENERAR]: 'Generar facturas masivas',
  [Permission.COBROS_ANULAR]: 'Anular facturas',
  // Pagos
  [Permission.PAGOS_READ]: 'Ver pagos',
  [Permission.PAGOS_CREATE]: 'Registrar pagos',
  [Permission.PAGOS_ANULAR]: 'Anular pagos',
  // Excepciones
  [Permission.EXCEPCIONES_READ]: 'Ver excepciones',
  [Permission.EXCEPCIONES_CREATE]: 'Crear excepciones',
  [Permission.EXCEPCIONES_UPDATE]: 'Editar excepciones',
  [Permission.EXCEPCIONES_DELETE]: 'Eliminar excepciones',
  // Visitas
  [Permission.VISITAS_READ]: 'Ver visitas',
  [Permission.VISITAS_CREATE]: 'Registrar visitas',
  // Catálogos
  [Permission.CATALOGOS_READ]: 'Ver catálogos',
  [Permission.CATALOGOS_CREATE]: 'Crear catálogos',
  [Permission.CATALOGOS_UPDATE]: 'Editar catálogos',
  [Permission.CATALOGOS_DELETE]: 'Eliminar catálogos',
  // Planes
  [Permission.PLANES_READ]: 'Ver planes',
  [Permission.PLANES_CREATE]: 'Crear planes',
  [Permission.PLANES_UPDATE]: 'Editar planes',
  [Permission.PLANES_DELETE]: 'Eliminar planes',
  // Tipos de servicio
  [Permission.TIPOS_SERVICIO_READ]: 'Ver tipos de servicio',
  [Permission.TIPOS_SERVICIO_CREATE]: 'Crear tipos de servicio',
  [Permission.TIPOS_SERVICIO_UPDATE]: 'Editar tipos de servicio',
  [Permission.TIPOS_SERVICIO_DELETE]: 'Eliminar tipos de servicio',
  // Comisiones
  [Permission.COMISIONES_READ]: 'Ver comisiones',
  // Cobrador
  [Permission.COBRADOR_READ]: 'Ver pantalla de cobro',
};

export const DEFAULT_ROLE_PERMISSIONS: Record<string, readonly Permission[]> = {
  admin: ALL_PERMISSIONS,
  // The cobrador only reads and operates its field workflow. Global invoice
  // and statement screens are intentionally excluded; Mi Cobro uses the
  // dedicated /api/cobrador surface instead.
  cobrador: [
    Permission.PAGOS_READ,
    Permission.PAGOS_CREATE,
    Permission.VISITAS_READ,
    Permission.VISITAS_CREATE,
    Permission.COBRADOR_READ,
  ],
};
